<template>
  <div>
    <gat-form v-model="isValid" ref="gatform" :readonly="!userHasAccess || task.LOCK_FLAG == 1" :isLoading="isLoading">
      <gat-group title="Task details" :collapsed="false">
        <v-layout wrap>
          <gat-select
            v-model="task.TYPE_ID"
            label="Type"
            :items="filteredTaskTypes"
            textFieldName="NAME"
            codeFieldName="ID"
            @item-selected="typeChanged"
            :required="true"
            :disabled="!isNew"
            size="xs12 sm6 xl3" />

          <gat-select
            v-model="task.STATUS_TYPE_ID"
            label="Status"
            :items="activeStatusTypes"
            textFieldName="NAME"
            codeFieldName="ID"
            :required="true"
            size="xs12 sm6 xl3" />

          <gat-client-select
            v-if="!applicationStore.globalSettings.SGL_CREWCHANGE_DEBTOR_FROM_HOTLIST"
            label="Debtor"
            v-model="task.DEBTOR_ID"
            category="0"
            :callId="callId"
            :required="true"
            :clearable="task.LOCK_FLAG != 1 && userHasAccess"
            showClientWarning
            showClientCreditLimit
            size="xs12 sm6 xl3" />
          <gat-select
            v-else
            :items="hotlistClients(0)"
            textFieldName="displayName"
            codeFieldName="value"
            label="Debtor"
            v-model="task.DEBTOR_ID"
            size="xs12 sm6 xl3" />

          <gat-select
            v-model="task.OPERATION"
            label="Operation"
            :items="operationOptions"
            textFieldName="text"
            codeFieldName="value"
            :required="true"
            size="xs12 sm6 xl3" />
        </v-layout>
      </gat-group>

      <gat-group v-if="isAccommodation" key="accommodation-details" title="Accommodation details" :collapsed="false">
        <v-layout wrap>
          <gat-select
            v-model="task.ROOM_TYPE_ID"
            label="Room type"
            :items="roomTypes"
            textFieldName="NAME"
            codeFieldName="ID"
            :required="true"
            size="xs12 sm12 xl4" />

          <gat-client-select
            label="Supplier"
            v-model="task.CLIENT_ID"
            :callId="callId"
            :clearable="true"
            :required="supplierRequired"
            @item-selected="supplierSelected"
            @input="supplierCleared"
            size="xs12 sm6 xl4" />

          <gat-edit
            v-model="task.CLIENT_NAME"
            label="Supplier name (can be set manually if supplier is not in connected clients)"
            :maxChars="applicationStore.getColumnLength('ccm_task.CLIENT_NAME')"
            :disabled="!!task.CLIENT_ID"
            :required="supplierRequired && !task.CLIENT_ID"
            size="xs12 sm6 xl4" />

          <gat-edit v-model="task.DATETIME_START" label="Check in" dateTimeEdit :required="true" size="xs12 sm6 xl4" />
          <gat-edit v-model="task.DATETIME_END" label="Check out" dateTimeEdit size="xs12 sm6 xl4" />
        </v-layout>
      </gat-group>

      <gat-group v-if="isTransport" key="transport-details" title="Transport details" :collapsed="false">
        <v-layout wrap>
          <gat-select
            v-model="task.TRANSPORTATION_TYPE_ID"
            label="Transportation type"
            :items="transportationTypes"
            textFieldName="NAME"
            codeFieldName="ID"
            :required="true"
            size="xs12 sm12 lg4 xl4" />

          <gat-client-select
            label="Supplier (transport)"
            v-model="task.CLIENT_ID"
            :callId="callId"
            :clearable="true"
            :required="supplierRequired"
            @item-selected="supplierSelected"
            @input="supplierCleared"
            size="xs12 sm6 lg4 xl4" />

          <gat-edit
            v-model="task.CLIENT_NAME"
            label="Supplier name (can be set manually if supplier is not in connected clients)"
            :maxChars="applicationStore.getColumnLength('ccm_task.CLIENT_NAME')"
            :disabled="!!task.CLIENT_ID"
            :required="supplierRequired && !task.CLIENT_ID"
            size="xs12 sm6 lg4 xl4" />

          <gat-select
            v-model="task.PICKUP_PLACE"
            label="Pickup place"
            :items="placeTypes"
            textFieldName="NAME"
            codeFieldName="NAME"
            :required="true"
            combobox
            size="xs12 sm6 xl6" />

          <gat-select
            v-model="task.DESTINATION_PLACE"
            label="Destination"
            :items="placeTypes"
            textFieldName="NAME"
            codeFieldName="NAME"
            combobox
            size="xs12 sm6 xl6" />

          <gat-edit
            v-model="task.DATETIME_START"
            label="Pickup date"
            dateTimeEdit
            :required="true"
            size="xs12 sm6 xl4" />

          <gat-edit v-model="task.DATETIME_END" label="Drop-off date" dateTimeEdit size="xs12 sm6 xl4" />
        </v-layout>
      </gat-group>

      <gat-group title="Additional details" :collapsed="false">
        <v-layout wrap>
          <gat-edit
            v-if="showPoNo"
            v-model="task.PO_NO"
            label="PO Number"
            :maxChars="applicationStore.getColumnLength('ccm_task.PO_NO')"
            size="xs12 sm6 xl3" />

          <gat-edit
            v-model="task.REMARKS"
            label="Remarks"
            :maxChars="10000"
            :rows="3"
            :required="remarksRequired"
            size="xs12" />
        </v-layout>
      </gat-group>

      <gat-group title="Dynamic fields" :collapsed="false" v-show="dynaFieldIds && dynaFieldIds.length > 0">
        <gat-dyna-fields
          v-model="task.DynaFields"
          componentName="pDynaFields.CrewChangeDynaTask"
          container="pDynaFields"
          :filterByIds="dynaFieldIds" />
      </gat-group>

      <gat-group title="Documents" :collapsed="false">
        <v-layout wrap>
          <embedded-doc-list
            v-if="!isNew && task && task.ID"
            :compact="false"
            location="crewchangetask"
            :parentId="taskIdString"
            :disabled="!userHasAccess || task.LOCK_FLAG == 1" />
        </v-layout>
        <span v-if="isNew">The task must be created in order to add documents.</span>
      </gat-group>

      <gat-group title="Connected people" :collapsed="false">
        <gat-grid
          :items="availablePeople"
          :columns="peopleColumns"
          :selectable="true"
          keyName="ID"
          :selectedKeys="selectedPeopleIds"
          @selectionChanged="peopleSelectionChanged"
          :fixedheader="true"
          height="30vh">
          <template slot="cell" slot-scope="props">
            <div v-if="props.column.field === 'PERSON_TYPE'">
              <span v-if="props.value === 0">Passenger</span>
              <span v-else-if="props.value === 1">Crew</span>
              <span v-else>{{ props.value }}</span>
            </div>
          </template>
        </gat-grid>
      </gat-group>

      <gat-grid
        v-if="applicationStore.userRight('CRCH.SHOW_EXPENSES')"
        :items="task.EXPENSES"
        :columns="expenseColumns"
        title="Expenses"
        :loading="isLoading"
        :fixedheader="true"
        :selectable="true"
        keyName="ID"
        @selectionChanged="expensesInUseChanged"
        :selectedKeys="selectedExpensesIds">
        <template slot="cell" slot-scope="props">
          <div v-if="props.column.field === 'QUANTITY'">
            <v-text-field hide-spin-buttons type="number" reverse dense v-model="props.row.QUANTITY" disabled />
          </div>
          <div v-else-if="props.column.field === 'Price'">
            <v-text-field
              hide-spin-buttons
              type="number"
              reverse
              dense
              v-model="props.row.Price"
              @input="priceChanged(props.row.ID)" />
          </div>
          <span v-else-if="props.column.field === 'MERGE_EXPENSES'">{{ Number(props.value) === 1 ? 'Yes' : '' }}</span>
          <span v-else>{{ props.value }}</span>
        </template>
      </gat-grid>

      <gat-bottom-panel
        :orgValue="orgTask"
        :newValue="task"
        :itemDescription="'task'"
        :saveBtn="true"
        :deleteBtn="true"
        :buttonsEnabled="!saving"
        :deleteDisabled="!task.ID || !userHasAccess || task.LOCK_FLAG == 1"
        :saveDisabled="!userHasAccess || task.LOCK_FLAG == 1 || !isValid"
        @saveClicked="saveTask"
        @deleteClicked="deleteTask" />
    </gat-form>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import { apiGet, apiPut, apiDelete } from '@/store/api';
import GatClientSelect from '@/app-components/GatClientSelect.vue';
import GatDynaFields from '@/app-components/dynafields/GatDynaFields.vue';
import EmbeddedDocList from '@/app-components/EmbeddedDocList.vue';
import { useToolbarStore } from '@/store/toolbarStore';
import { useApplicationStore } from '@/store/applicationStore';
import { useHelperStore } from '@/store/helperStore';
import { usePortcallStore } from '@/store/portcallStore';
import { getCrewChangeTasks } from '@/services/api/api-calls/getCrewChangeTasks';
import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';

export default {
  name: 'CrewChangeTask',
  props: ['taskId', 'callId', 'isNew', 'ccType'],
  components: { GatClientSelect, GatDynaFields, EmbeddedDocList },
  setup() {
    const toolbarStore = useToolbarStore();
    const applicationStore = useApplicationStore();
    const helperStore = useHelperStore();
    const portcallStore = usePortcallStore();
    return {
      toolbarStore,
      applicationStore,
      helperStore,
      portcallStore,
    };
  },
  data() {
    return {
      task: {},
      orgTask: {},
      isValid: null,
      saving: false,
      isLoading: true,
      loaded: false,
      operationOptions: [
        { text: 'Embarking', value: 0 },
        { text: 'Disembarking', value: 1 },
        { text: 'Other', value: 2 },
      ],
      taskTypes: [],
      statusTypes: [],
      roomTypes: [],
      transportationTypes: [],
      placeTypes: [],
      availablePeople: [],
      selectedPeopleIds: [],
      selectedExpensesIds: [],
      dynaFieldIds: [],
      expenseColumns: [
        { header: 'Name', field: 'EXPENSE_GROUP_NAME' },
        { header: 'Template', field: 'EXPENSE_TEMPLATE_NAME' },
        { header: 'Price tariff', field: 'PRICE_GROUP_NAME' },
        { header: 'Qty', field: 'QUANTITY', dataType: 'number' },
        { header: 'Price', field: 'Price', dataType: 'number' },
        { header: 'Amount', field: 'AMOUNT', dataType: 'number' },
        { header: 'Merge', field: 'MERGE_EXPENSES', dataType: 'boolean' },
        { header: 'Default supplier', field: 'ELT_SUPPLIER_NAME' },
      ],
      peopleColumns: [],
    };
  },
  async activated() {
    this.dynaFieldIds = [];
    if (!this.loaded) {
      await this.loadLookups();
      await this.loadTask();
    }

    this.toolbarStore.setBackNavigation([
      { text: 'port call', href: `/call/${this.callId}` },
      { text: 'crew change', href: `/call/${this.callId}/crewchange` },
    ]);
  },
  deactivated() {
    this.toolbarStore.setBackNavigation(undefined);
    this.loaded = false;
    this.task = {};
    this.orgTask = {};
    this.selectedPeopleIds = [];
    this.selectedExpensesIds = [];
    this.dynaFieldIds = [];
    this.toolbarStore.setToolbarNavBtns({ previousState: true });
  },
  computed: {
    currentCcType() {
      // Read from $route.query directly — the ccType prop may be stale in keep-alive
      if (this.$route.query.ccType !== undefined) {
        return Number(this.$route.query.ccType);
      }
      return this.ccType;
    },
    activeStatusTypes() {
      return this.statusTypes.filter((st) => !st.INACTIVE);
    },
    filteredTaskTypes() {
      if (this.isNew && this.currentCcType) {
        return this.taskTypes.filter((t) => t.CC_TYPE === this.currentCcType);
      }
      if (!this.isNew && this.task.TYPE_ID) {
        const currentType = this.taskTypes.find((t) => t.ID === this.task.TYPE_ID);
        if (currentType) {
          return this.taskTypes.filter((t) => t.CC_TYPE === currentType.CC_TYPE);
        }
      }
      return this.taskTypes;
    },
    isAccommodation() {
      if (!this.task.TYPE_ID) return this.currentCcType === 1;
      const type = this.taskTypes.find((t) => t.ID === this.task.TYPE_ID);
      return type && type.CC_TYPE === 1;
    },
    isTransport() {
      if (!this.task.TYPE_ID) return this.currentCcType === 2;
      const type = this.taskTypes.find((t) => t.ID === this.task.TYPE_ID);
      return type && type.CC_TYPE === 2;
    },
    taskIdString() {
      if (this.task.ID) return this.task.ID.toString();
      return '';
    },
    remarksRequired() {
      if (!this.task.STATUS_TYPE_ID) return false;
      const status = this.statusTypes.find((st) => st.ID === this.task.STATUS_TYPE_ID);
      return status && status.STATUS === 2;
    },
    supplierRequired() {
      if (!this.task.EXPENSES) return false;
      return this.task.EXPENSES.some((exp) => exp.USE_EXPENSE === 1 && exp.ELT_SUPPLIER_MANDATORY === 1);
    },
    showPoNo() {
      if (!this.task.TYPE_ID) return false;
      const type = this.taskTypes.find((t) => t.ID === this.task.TYPE_ID);
      return type && type.USE_PO_NO === 1 && this.applicationStore.globalSettings.SGL_EXP_USE_PO_NO === 1;
    },
    userHasAccess() {
      return this.applicationStore.userRight('CRCH.ACCESS');
    },
  },
  methods: {
    hotlistClients(category) {
      const clients = [];
      // eslint-disable-next-line array-callback-return
      this.portcallStore.hotlist.map((client) => {
        if (client.CATEGORY == category) {
          const menuItem = {
            value: client.CLIENT_ID,
            displayName: client.C_NAME,
          };
          if (client.C_ACCOUNT_NO) {
            menuItem.displayName = `${menuItem.displayName} (${client.C_ACCOUNT_NO})`;
          }
          clients.push(menuItem);
        }
      });
      return clients;
    },
    sortedAvailablePeople() {
      return [...this.availablePeople].sort((a, b) => {
        const aSelected = this.selectedPeopleIds.includes(a.ID) ? 0 : 1;
        const bSelected = this.selectedPeopleIds.includes(b.ID) ? 0 : 1;
        return aSelected - bSelected;
      });
    },
    async loadLookups() {
      try {
        const [types, statuses, rooms, transports, places] = await Promise.all([
          apiGet('crewchange/taskTypes'),
          apiGet('crewchange/statusTypes'),
          apiGet('crewchange/roomTypes'),
          apiGet('crewchange/transportationTypes'),
          apiGet('crewchange/placeTypes'),
        ]);
        this.taskTypes = types;
        this.statusTypes = statuses;
        this.roomTypes = rooms;
        this.transportationTypes = transports;
        this.placeTypes = places;
      } catch (e) {
        // Lookups failed — form will show empty selects
      }
    },
    async loadTask() {
      this.loaded = true;
      this.isLoading = true;
      this.task = {};
      this.orgTask = {};
      this.selectedPeopleIds = [];
      this.selectedExpensesIds = [];

      if (this.isNew) {
        if (this.$refs.gatform) {
          this.$refs.gatform.resetValidation();
        }
        // Set defaults - STATUS field may come as number or string from the API
        const defaultStatusType = this.statusTypes.find((st) => Number(st.STATUS) === 0 && !st.INACTIVE);
        let defaultStatusId = null;
        if (defaultStatusType) {
          defaultStatusId = defaultStatusType.ID;
        } else if (this.activeStatusTypes.length > 0) {
          defaultStatusId = this.activeStatusTypes[0].ID;
        }
        const defaultPrincipleId = await apiGet(
          `/crewchange/people/default-principle/${this.applicationStore.globalSettings.SGL_DEFAULT_PAYING_PARTY}/${this.callId}`
        ).catch(() => null);

        this.task = {
          OPERATION: 0,
          STATUS_TYPE_ID: defaultStatusId,
          DEBTOR_ID: defaultPrincipleId || null,
          CLIENT_ID: null,
          CLIENT_NAME: null,
          DynaFields: [],
          EXPENSES: [],
        };
        await this.loadAvailablePeople();
        this.isLoading = false;
      } else {
        try {
          const result = await apiGet(`/crewchange/task/${this.taskId}`);
          this.task = result;
          this.orgTask = GlobalHelperFunctions.duplicateViaJson(this.task);
          this.filterDynaFields();
          this.setInUsedExpenses();

          // Load available people for this portcall
          await this.loadAvailablePeople();

          // Set selected people
          if (this.task.PEOPLE) {
            this.selectedPeopleIds = this.task.PEOPLE.map((p) => p.PERSON_ID);
            this.availablePeople = this.sortedAvailablePeople();
          }
          this.normalizeExpenseNumericFields();
        } catch (e) {
          // Task load failed
        } finally {
          this.isLoading = false;
          this.$nextTick(() => {
            if (this.$refs.gatform) {
              this.$refs.gatform.validate();
            }
          });
        }
      }
    },
    async loadAvailablePeople() {
      try {
        const clientId = this.clientId();
        const result = await apiGet(`crewchange/call/${this.callId}/people/${clientId}`);
        this.availablePeople = result.persons || [];
        this.buildPeopleColumns();
      } catch (e) {
        this.availablePeople = [];
      }
    },
    buildPeopleColumns() {
      const cols = [
        { header: 'Type', field: 'PERSON_TYPE', dataType: 'number' },
        { header: 'Lastname', field: 'LASTNAME' },
        { header: 'Firstname', field: 'FIRSTNAME' },
        { header: 'Debtor', field: 'Debtor' },
        { header: 'Rank', field: 'RANK' },
        { header: 'Gender', field: 'GENDER' },
        { header: 'Nationality', field: 'NATIONALITY' },
        { header: 'Accommodations', field: 'Accom', dataType: 'number' },
        { header: 'Transports', field: 'Transp', dataType: 'number' },
      ];

      // Add dynamic field columns (same pattern as CrewChangePeopleList)
      const peopleDynafields = [];
      this.availablePeople.forEach((person) => {
        Object.keys(person).forEach((key) => {
          if (key.startsWith('dyn_') && !peopleDynafields.includes(key)) {
            peopleDynafields.push(key);
          }
        });
      });

      for (let i = 0; i < this.helperStore.dynafields.length; i++) {
        const dynafield = this.helperStore.dynafields[i];
        if (dynafield.COMPONENT_NAME === 'pDynaFields.CrewChangeDynaPerson') {
          if (peopleDynafields.includes(`dyn_${dynafield.ID}`)) {
            cols.push({
              header: dynafield.CAPTION,
              field: `dyn_${dynafield.ID}`,
              dataType: this.getDynaDataType(dynafield.FIELD_TYPE),
            });
          }
        }
      }

      this.peopleColumns = cols;
    },
    getDynaDataType(fieldType) {
      switch (fieldType) {
        case 3:
          return 'date';
        case 12:
          return 'datetime';
        default:
          return undefined;
      }
    },
    async typeChanged(event) {
      if (!event) return;
      if (!this.isNew) return;
      const type = event;

      // Set defaults from type
      if (type.DEFAULT_OPERATION !== undefined && type.DEFAULT_OPERATION !== null) {
        this.task.OPERATION = type.DEFAULT_OPERATION;
      }
      if (type.DEFAULT_ROOM_TYPE && type.CC_TYPE === 1) {
        this.task.ROOM_TYPE_ID = type.DEFAULT_ROOM_TYPE;
      }
      if (type.DEFAULT_TRANSPORTATION_TYPE && type.CC_TYPE === 2) {
        this.task.TRANSPORTATION_TYPE_ID = type.DEFAULT_TRANSPORTATION_TYPE;
      }

      // Clear the other type field
      if (type.CC_TYPE === 1) {
        this.task.TRANSPORTATION_TYPE_ID = null;
      } else if (type.CC_TYPE === 2) {
        this.task.ROOM_TYPE_ID = null;
      }

      // Load dynamic fields for this type
      this.isLoading = true;
      try {
        const ids = await apiGet(`/crewchange/task/type/${type.ID}/dynafields`);
        this.dynaFieldIds = ids;
      } catch (e) {
        this.dynaFieldIds = [];
      }

      // Load expense templates for this type
      try {
        const expenses = await apiGet(`/crewchange/task/type/${type.ID}/expenses`);
        this.task.EXPENSES = expenses;
        this.normalizeExpenseNumericFields();
        this.setInUsedExpenses();
        this.updateExpenseQtyFromConnections();
        this.updateSupplierFromExpenses();
      } catch (e) {
        this.task.EXPENSES = [];
      }
      this.isLoading = false;
    },
    setInUsedExpenses() {
      const tmpArr = [];
      if (this.task.EXPENSES) {
        for (let i = 0; i < this.task.EXPENSES.length; i++) {
          if (this.task.EXPENSES[i].USE_EXPENSE === 1) {
            tmpArr.push(this.task.EXPENSES[i].ID);
          }
        }
      }
      this.selectedExpensesIds = tmpArr;
    },
    expensesInUseChanged(expenseIds) {
      if (this.saving) return;
      if (!this.task.EXPENSES) return;

      this.selectedExpensesIds = expenseIds;
      for (let i = 0; i < this.task.EXPENSES.length; i++) {
        const idx = this.selectedExpensesIds.indexOf(this.task.EXPENSES[i].ID);
        this.task.EXPENSES[i].USE_EXPENSE = idx !== -1 ? 1 : 0;
      }
      this.updateSupplierFromExpenses();
    },
    priceChanged(ID) {
      const idx = this.task.EXPENSES.findIndex((x) => x.ID === ID);
      if (idx >= 0) {
        const qty = Number(this.task.EXPENSES[idx].QUANTITY) || 0;
        const price = Number(this.task.EXPENSES[idx].Price) || 0;
        this.task.EXPENSES[idx].AMOUNT = qty * price;
      }
    },
    parseNumericValue(value) {
      if (value === null || value === undefined || value === '') return 0;
      if (typeof value === 'number') return value;

      const valueStr = String(value).trim();
      if (valueStr === '') return 0;

      let normalized = valueStr.replace(/\s/g, '');
      if (normalized.includes(',') && normalized.includes('.')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else if (normalized.includes(',')) {
        normalized = normalized.replace(',', '.');
      }

      const numericValue = Number(normalized);
      return Number.isFinite(numericValue) ? numericValue : 0;
    },
    normalizeExpenseNumericFields() {
      if (!this.task.EXPENSES) return;

      for (let i = 0; i < this.task.EXPENSES.length; i++) {
        const expense = this.task.EXPENSES[i];
        expense.QUANTITY = this.parseNumericValue(expense.QUANTITY);
        expense.Price = this.parseNumericValue(expense.Price);
        expense.AMOUNT = this.parseNumericValue(expense.AMOUNT);
      }
    },
    updateExpenseQtyFromConnections() {
      if (!this.task.EXPENSES) return;

      const selectedPeopleCount = this.selectedPeopleIds.length;
      for (let i = 0; i < this.task.EXPENSES.length; i++) {
        const expense = this.task.EXPENSES[i];
        if (Number(expense.USE_QTY_FROM_CONNECTIONS) === 1) {
          expense.QUANTITY = selectedPeopleCount;
        }

        const qty = Number(expense.QUANTITY) || 0;
        const price = Number(expense.Price) || 0;
        expense.AMOUNT = qty * price;
      }
    },
    updateSupplierFromExpenses() {
      if (!this.task.EXPENSES) return;
      const defaultSuppliers = [];
      for (let i = 0; i < this.task.EXPENSES.length; i++) {
        const exp = this.task.EXPENSES[i];
        if (exp.USE_EXPENSE === 1 && exp.ELT_SUPPLIER_ID) {
          defaultSuppliers.push(exp.ELT_SUPPLIER_ID);
        }
      }
      if (defaultSuppliers.length > 0) {
        const allEqual = defaultSuppliers.every((id) => id === defaultSuppliers[0]);
        if (allEqual) {
          this.task.CLIENT_ID = defaultSuppliers[0];
          const matchingExp = this.task.EXPENSES.find((exp) => exp.ELT_SUPPLIER_ID === defaultSuppliers[0]);
          if (matchingExp && matchingExp.ELT_SUPPLIER_NAME) {
            this.task.CLIENT_NAME = matchingExp.ELT_SUPPLIER_NAME;
          }
        }
      }
    },
    supplierSelected(item) {
      if (item) {
        this.task.CLIENT_NAME = item.NAME;
      }
    },
    supplierCleared(value) {
      if (!value) {
        this.task.CLIENT_NAME = null;
      }
    },
    peopleSelectionChanged(selectedIds) {
      this.selectedPeopleIds = selectedIds;
      this.updateExpenseQtyFromConnections();
    },
    filterDynaFields() {
      this.dynaFieldIds = [];
      if (this.task.DynaFields) {
        for (let i = 0; i < this.task.DynaFields.length; i++) {
          this.dynaFieldIds.push(this.task.DynaFields[i].FIELD_ID);
        }
      }
    },
    clientId() {
      if (
        this.applicationStore.user.internal ||
        this.applicationStore.userRight('CRCH.ACCESS') ||
        this.applicationStore.userRight('CRCH.ACCESS_OWN_TASK')
      ) {
        return 0;
      }
      return this.applicationStore.user.externalClientId || null;
    },
    async saveTask() {
      this.$refs.gatform.validate();
      if (!this.isValid) return;

      this.saving = true;
      this.isLoading = true;
      const taskData = { ...this.task };

      try {
        const result = await apiPut('/CrewChange/Task', {
          value: taskData,
          orgValue: this.orgTask,
          dynaValues: this.task.DynaFields,
          orgDynaValues: this.orgTask.DynaFields,
          expenses: this.task.EXPENSES,
          orgExpenses: this.orgTask.EXPENSES,
          people: this.selectedPeopleIds,
          portcallId: parseInt(this.callId, 10),
        });

        this.isLoading = false;
        if (result.resultCategory === 1) {
          if (this.isNew) {
            this.task = {};
            this.orgTask = {};
          } else {
            this.task = taskData;
            this.orgTask = GlobalHelperFunctions.duplicateViaJson(this.task);
          }

          this.$nextTick(() => {
            getCrewChangeTasks({ callId: this.callId, clientId: this.clientId() });
            this.$router.push({ name: 'crewChange', params: { callId: this.callId } });
            this.saving = false;
          });
        } else {
          this.saving = false;
        }
      } catch (e) {
        this.isLoading = false;
        this.saving = false;
      }
    },
    async deleteTask() {
      this.isLoading = true;
      try {
        const result = await apiDelete(`/crewchange/task/${this.task.ID}`);
        this.isLoading = false;
        if (result.resultCategory === 1) {
          this.task = {};
          this.orgTask = {};
          this.$nextTick(() => {
            getCrewChangeTasks({ callId: this.callId, clientId: this.clientId() });
            this.$router.push({ name: 'crewChange', params: { callId: this.callId } });
          });
        }
      } catch (e) {
        this.isLoading = false;
      }
    },
  },
  watch: {
    $route: {
      handler(to, from) {
        if (to.fullPath !== from.fullPath && (to.name === 'newCrewChangeTask' || to.name === 'crewChangeTask')) {
          this.loaded = false;
          this.task = {};
          this.orgTask = {};
          this.selectedPeopleIds = [];
          this.selectedExpensesIds = [];
          this.dynaFieldIds = [];
          this.loadLookups().then(() => this.loadTask());
        }
      },
    },
    taskId: {
      handler(newTaskId) {
        if (newTaskId) {
          this.loadTask();
        }
      },
    },
  },
};
</script>
